import { RequestPaymentModel } from '@bootpay/client-js/models/bootpay-interface';
import { GiftOrder, GiftOrderItem } from '@shared/api/giftOrder/giftOrder.interface';
import { User } from '@shared/api/user/user.interface';
import { dateFormatBootPay } from '@shared/utils/bootpayUtil';

interface IParams {
  giftOrder: GiftOrder;
  userProfile: User<string>;
  /** 결제수단 고정(예: 대리주문 모드의 'vbank') 또는 허용 목록(권역 브랜드 정책). 미지정 시 결제창에서 선택 */
  method?: string | string[];
}

// giftItem에서 ID 추출 헬퍼 함수
const getGiftItemId = (giftItem: GiftOrderItem['giftItem']): string => {
  if (typeof giftItem === 'string') {
    return giftItem;
  }
  if (giftItem && typeof giftItem === 'object' && '_id' in giftItem) {
    return (giftItem as { _id: string })._id;
  }
  return '';
};

/**
 * Gift Order용 Bootpay 결제 요청 DTO 생성 (다중 상품 지원)
 * - REACT_APP_BOOTPAY_GIFT_APPLICATION_ID 사용
 */
export const getGiftBootpayRequestPaymentDto = ({
  giftOrder,
  userProfile,
  method,
}: IParams): RequestPaymentModel => {
  // 주문명 생성
  const firstItem = giftOrder.orderDetails[0];
  const firstItemName = firstItem?.giftItemEmbed.name || '답례품';
  const firstItemQty = firstItem?.quantity || 1;

  // 단일 상품: "상품명 x 10개", 다중 상품: "상품명 외 N건"
  const orderName =
    giftOrder.orderDetails.length > 1
      ? `${firstItemName} 외 ${giftOrder.orderDetails.length - 1}건`
      : `${firstItemName} x ${firstItemQty}개`;

  // Bootpay items 배열 생성 (각 상품에 수량 표시). 옵션 추가금은 단가에 합산.
  const items = giftOrder.orderDetails.map((detail) => {
    const optionDelta = (detail.selectedOptions ?? []).reduce(
      (sum, opt) => sum + (opt.priceDelta || 0),
      0,
    );
    return {
      id: getGiftItemId(detail.giftItem),
      name: `${detail.giftItemEmbed.name} x ${detail.quantity}개`,
      price: detail.giftItemEmbed.priceRetail + optionDelta,
      qty: detail.quantity,
    };
  });

  // Bootpay는 price(총 결제액) === Σ(items.price × qty) 를 서버에서 검증한다.
  // 단가에 못 담는 차액(권역 배송비, 소량주문 추가금)을 보정 항목으로 추가해 정확히 일치시킨다.
  const itemsTotal = items.reduce((sum, it) => sum + it.price * it.qty, 0);
  let remainder = giftOrder.paymentDetail.paymentByCash - itemsTotal;
  const deliveryFeeTotal = giftOrder.deliveryFee?.total ?? 0;
  if (deliveryFeeTotal > 0 && remainder >= deliveryFeeTotal) {
    items.push({ id: 'delivery-fee', name: '배송비', price: deliveryFeeTotal, qty: 1 });
    remainder -= deliveryFeeTotal;
  }
  if (remainder > 0) {
    items.push({
      id: 'surcharge',
      name: '소량주문 추가금',
      price: remainder,
      qty: 1,
    });
  }

  return {
    ...(method ? { method } : {}),
    price: giftOrder.paymentDetail.paymentByCash, // 총 결제할 금액
    application_id: process.env.REACT_APP_BOOTPAY_GIFT_APPLICATION_ID, // Gift 전용 Application ID
    order_name: orderName,
    pg: '나이스페이',
    items,
    // 구매자 정보는 주문서의 주문자(전화주문이면 상주)를 우선 — 로그인 프로필은 폴백
    user: {
      username: giftOrder.receiverName || userProfile.info.name || '',
      email: '',
      phone: giftOrder.receiverPhoneNumber || userProfile.info.phoneNumber || '',
      addr: '',
    },
    tax_free:
      giftOrder.paymentDetail.paymentByCash -
      (giftOrder.paymentDetail.taxableAmount ?? 0),
    order_id: giftOrder._id,
    extra: {
      open_type: window.opener ? 'popup' : 'iframe',
      browser_open_type: [
        {
          browser: 'instagram',
          open_type: 'popup',
        },
        {
          browser: 'facebook',
          open_type: 'popup',
        },
        {
          browser: 'kakaotalk',
          open_type: 'popup',
        },
        {
          browser: 'naver',
          open_type: 'popup',
        },
        {
          browser: 'mobile_safari',
          open_type: 'popup',
        },
        {
          browser: 'mobile_chrome',
          open_type: 'iframe',
        },
      ],
      display_success_result: true,
      display_error_result: true,
      deposit_expiration: dateFormatBootPay(new Date(Date.now() + 1 * 24 * 3600 * 1000)),
      card_quota: '0', // 일시불
      // 현금영수증(소득공제) 번호 프리필은 불가 — extra.cash_receipt_type/identity_no는
      // 나이스페이 계좌이체·네이버페이 한정 파라미터라 가상계좌 발급창엔 적용되지
      // 않는다(실테스트로 확인, 2026-08). 가상계좌 현금영수증은 고객이 창에서 직접 입력.
    },
  };
};
